(ns io.daskul.webapp.widgets.form.lookup-edit
  "Inline edit form for a record of another aggregate — the patch-schema twin of
   lookup-inline-create (#974).

   Same shape and the same generic form: fetch the schema, render the fields,
   submit, call back. The differences are the ones the patch path forces —
   the schema is `/_schema/<resource>/patch`, the form starts from the record,
   protected fields arrive marked :readonly and must be neither rendered nor
   submitted (the server rejects them as a disallowed key, #831), and the write
   is a PATCH of only what the schema allows.

   Exists so a surface that already shows a record can let you correct it in
   place, instead of sending you to its detail page and back."
  (:require
   [uix.core :as uix :refer [defui $]]
   [ajax.core :as ajax]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.form.errors :as form.errors]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.config.subs :as config.subs]
   [uix.re-frame :as urf]))

(defui lookup-inline-edit
  "Props:
   - resource: string  - REST resource name (e.g. \"discounts\")
   - record: map       - the row being edited; must carry :id
   - on-saved: (fn [])   - called after a successful patch
   - on-cancel: (fn [])
   - form-field-component: component - renders individual fields
   - hidden-fields: set - field keywords to skip rendering (values are not sent)"
  [{:keys [resource record on-saved on-cancel form-field-component hidden-fields]}]
  (let [aggregates (urf/use-subscribe [::config.subs/aggregates])
        [schema set-schema!] (uix/use-state nil)
        [schema-loading? set-schema-loading!] (uix/use-state true)
        [schema-error set-schema-error!] (uix/use-state nil)
        [form-data set-form-data!] (uix/use-state {})
        [saving? set-saving!] (uix/use-state false)
        [form-error set-form-error!] (uix/use-state nil)
        agg-key (keyword resource)
        record-id (:id record)]

    (uix/use-effect
     (fn []
       (set-schema-loading! true)
       (ajax/GET (str (agg.events/get-api-base) "/_schema/" resource "/patch")
                 {:headers (agg.events/get-auth-header)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :handler (fn [response]
                             (let [normalized (agg.events/normalize-schema response aggregates)]
                               (set-schema! normalized)
                               ;; Seed from the record, but only the keys this
                               ;; schema owns: anything else would be sent back
                               ;; and rejected.
                               (set-form-data!
                                (select-keys record (map :name (:fields normalized))))
                               (set-schema-loading! false)))
                  :error-handler (fn [_err]
                                   (set-schema-error! "Failed to load form")
                                   (set-schema-loading! false))})
       js/undefined)
     [resource aggregates record])

    ($ ui/box {:p "md"}
      (cond
        schema-loading?
        ($ ui/centered-loader {:label "Loading form..."})

        schema-error
        ($ ui/stack {:gap "sm"}
          ($ ui/error-alert schema-error)
          ($ ui/button {:type "button" :variant "subtle" :color "gray" :size "sm" :onClick on-cancel}
            "Cancel"))

        :else
        (let [fields (cond->> (:fields schema)
                       true (remove :readonly)
                       (seq hidden-fields)
                       (remove #(contains? hidden-fields (:name %))))
              editable-keys (set (map :name fields))
              render-field form-field-component
              render-one
              (fn [field]
                (let [field-key (:name field)
                      raw-err (get-in form-error [:field-errors field-key])]
                  ($ render-field
                    {:key (name field-key)
                     :agg-key agg-key
                     :field field
                     :value (get form-data field-key)
                     :on-change #(set-form-data! (assoc form-data field-key %))
                     :set-form-field! #(set-form-data! (assoc form-data %1 %2))
                     :form-data form-data
                     :external-error (form.errors/humanize-error raw-err)
                     :on-clear-error #(set-form-error!
                                        (update form-error :field-errors dissoc field-key))})))
              handle-submit
              (fn []
                (set-saving! true)
                (set-form-error! nil)
                (ajax/PATCH (str (agg.events/get-api-base) "/" resource "/" record-id)
                            {:params (select-keys form-data editable-keys)
                             :headers (agg.events/get-auth-header)
                             :format (ajax/json-request-format)
                             :response-format (ajax/json-response-format {:keywords? true})
                             :handler (fn [_response]
                                        (set-saving! false)
                                        (on-saved))
                             :error-handler (fn [error]
                                              (set-saving! false)
                                              (set-form-error!
                                               (form.errors/parse-error-response error)))}))
              error-msg (form.errors/form-level-error-message form-error fields)]
          ($ :form {:onSubmit (fn [e]
                                (.preventDefault e)
                                (.stopPropagation e)
                                (handle-submit))}
            ($ ui/stack {:gap "sm"}
              (when error-msg
                ($ ui/error-alert {:mb "xs"} error-msg))
              (for [field fields]
                (render-one field))
              ($ ui/group {:justify "flex-end" :gap "sm"}
                ($ ui/button {:type "button" :variant "subtle" :color "gray" :size "sm"
                              :onClick (fn []
                                         (set-form-error! nil)
                                         (on-cancel))
                              :disabled saving?}
                  "Cancel")
                ($ ui/button {:type "submit" :size "sm" :loading saving?}
                  "Save")))))))))
